import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Button, Dialog, DialogContent, DialogActions,
  TextField, IconButton, Chip, Stack, Paper, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress,
  Alert, Snackbar, Tooltip, Select, MenuItem, FormControl,
  InputLabel, OutlinedInput, Checkbox, ListItemText, Switch,
  FormControlLabel, Card, CardContent, Divider, LinearProgress,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingIcon from '@mui/icons-material/Pending';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import {
  getAllAgreements, createAgreement, updateAgreement, deleteAgreement
} from '../../services/agreementService';
import { getAllUsers } from '../../services/authService';
import { getUserData } from '../../services/storageService';

const API_URL = process.env.REACT_APP_API_URL || 'https://backend-c-0mxm.onrender.com/api';

const AgreementManagement = ({ isMobile }) => {
  const [agreements, setAgreements] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [detailDialog, setDetailDialog] = useState(null);
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [form, setForm] = useState({
    title: '',
    description: '',
    assignedUsers: [],
    file: null,
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [agRes, usrRes] = await Promise.all([
        getAllAgreements(),
        getAllUsers(),
      ]);
      if (agRes.success) setAgreements(agRes.data);
      if (usrRes.success) setUsers(usrRes.data.filter(u => u.role !== 'admin'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const showNotif = (message, severity = 'success') => {
    setNotification({ open: true, message, severity });
  };

  const resetForm = () => {
    setForm({ title: '', description: '', assignedUsers: [], file: null });
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setDialogOpen(true);
  };

  const openEdit = (ag) => {
    setForm({
      title: ag.title,
      description: ag.description || '',
      assignedUsers: ag.assignedUsers?.map(u => u._id) || [],
      file: null,
    });
    setEditingId(ag._id);
    setDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) return showNotif('Vui lòng nhập tiêu đề', 'error');
    if (!editingId && !form.file) return showNotif('Vui lòng upload file PDF', 'error');

    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('title', form.title);
      formData.append('description', form.description);
      formData.append('assignedUsers', JSON.stringify(form.assignedUsers));
      if (form.file) formData.append('file', form.file);

      const res = editingId
        ? await updateAgreement(editingId, formData)
        : await createAgreement(formData);

      if (res.success) {
        showNotif(editingId ? 'Cập nhật thành công' : 'Tạo thành công');
        setDialogOpen(false);
        resetForm();
        fetchData();
      } else {
        showNotif(res.error?.message || 'Có lỗi xảy ra', 'error');
      }
    } catch (e) {
      showNotif('Có lỗi xảy ra', 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const res = await deleteAgreement(id);
      if (res.success) {
        showNotif('Đã xóa');
        fetchData();
      } else {
        showNotif(res.error?.message || 'Lỗi', 'error');
      }
    } catch (e) {
      showNotif('Lỗi xóa', 'error');
    }
    setConfirmDelete(null);
  };

  const handleToggleActive = async (ag) => {
    const formData = new FormData();
    formData.append('isActive', !ag.isActive);
    const res = await updateAgreement(ag._id, formData);
    if (res.success) {
      showNotif(ag.isActive ? 'Đã tắt' : 'Đã bật');
      fetchData();
    }
  };

  const getAcceptanceStats = (ag) => {
    const targetUsers = ag.assignedUsers?.length > 0
      ? ag.assignedUsers
      : users;
    const total = targetUsers.length;
    const accepted = ag.acceptances?.filter(a => a.version === ag.version).length || 0;
    return { total, accepted };
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h6" fontWeight={700} color="#1e293b">
            Quản lý Điều khoản & Hợp đồng
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Upload PDF điều khoản và gán cho nhân viên
          </Typography>
        </Box>
        <Button
          variant="contained" startIcon={<AddIcon />}
          onClick={openCreate}
          sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, bgcolor: '#1e3a8a' }}
        >
          Thêm điều khoản
        </Button>
      </Stack>

      {/* Stats */}
      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}>
        <Card sx={{ minWidth: 140, borderRadius: 2 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Tổng điều khoản</Typography>
            <Typography variant="h5" fontWeight={700} color="#1e3a8a">{agreements.length}</Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 140, borderRadius: 2 }}>
          <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
            <Typography variant="caption" color="text.secondary">Đang hoạt động</Typography>
            <Typography variant="h5" fontWeight={700} color="#059669">{agreements.filter(a => a.isActive).length}</Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2, overflow: 'hidden' }}>
        <Table size={isMobile ? 'small' : 'medium'}>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f1f5f9' }}>
              <TableCell sx={{ fontWeight: 700 }}>Tiêu đề</TableCell>
              {!isMobile && <TableCell sx={{ fontWeight: 700 }}>File</TableCell>}
              <TableCell sx={{ fontWeight: 700 }}>Phiên bản</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Gán cho</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Đã đồng ý</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Trạng thái</TableCell>
              <TableCell sx={{ fontWeight: 700 }} align="right">Thao tác</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {agreements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">Chưa có điều khoản nào</Typography>
                </TableCell>
              </TableRow>
            ) : (
              agreements.map(ag => {
                const stats = getAcceptanceStats(ag);
                const percent = stats.total > 0 ? Math.round((stats.accepted / stats.total) * 100) : 0;
                return (
                  <TableRow key={ag._id} hover>
                    <TableCell>
                      <Typography fontWeight={600} variant="body2">{ag.title}</Typography>
                      {ag.description && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {ag.description.substring(0, 60)}{ag.description.length > 60 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    {!isMobile && (
                      <TableCell>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <PictureAsPdfIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                          <Typography variant="caption" noWrap sx={{ maxWidth: 120 }}>{ag.fileName}</Typography>
                          <Typography variant="caption" color="text.secondary">({formatFileSize(ag.fileSize)})</Typography>
                        </Stack>
                      </TableCell>
                    )}
                    <TableCell>
                      <Chip label={`v${ag.version}`} size="small" sx={{ fontWeight: 700, bgcolor: '#e0f2fe', color: '#0369a1' }} />
                    </TableCell>
                    <TableCell>
                      {ag.assignedUsers?.length > 0 ? (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                          {ag.assignedUsers.slice(0, 2).map(u => (
                            <Chip key={u._id} label={u.fullName} size="small" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                          ))}
                          {ag.assignedUsers.length > 2 && (
                            <Chip label={`+${ag.assignedUsers.length - 2}`} size="small" sx={{ fontSize: '0.7rem' }} />
                          )}
                        </Stack>
                      ) : (
                        <Chip label="Tất cả" size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }} />
                      )}
                    </TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="caption" fontWeight={600}>{stats.accepted}/{stats.total}</Typography>
                          <Typography variant="caption" color="text.secondary">({percent}%)</Typography>
                        </Stack>
                        <LinearProgress
                          variant="determinate" value={percent}
                          sx={{ height: 4, borderRadius: 2, bgcolor: '#e2e8f0',
                            '& .MuiLinearProgress-bar': { bgcolor: percent === 100 ? '#16a34a' : '#f59e0b', borderRadius: 2 }
                          }}
                        />
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={ag.isActive ? 'Bật' : 'Tắt'}
                        size="small"
                        sx={{
                          fontWeight: 600, cursor: 'pointer',
                          bgcolor: ag.isActive ? '#dcfce7' : '#fee2e2',
                          color: ag.isActive ? '#16a34a' : '#dc2626',
                        }}
                        onClick={() => handleToggleActive(ag)}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        <Tooltip title="Chi tiết">
                          <IconButton size="small" onClick={() => setDetailDialog(ag)}>
                            <VisibilityIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Xem PDF">
                          <IconButton size="small" onClick={() => {
                            const user = getUserData();
                            const token = user?.userId || user?.token;
                            window.open(`${API_URL}/agreements/${ag._id}/file?token=${token}`, '_blank');
                          }}>
                            <PictureAsPdfIcon sx={{ fontSize: 18, color: '#dc2626' }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Sửa">
                          <IconButton size="small" onClick={() => openEdit(ag)}>
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Xóa">
                          <IconButton size="small" onClick={() => setConfirmDelete(ag._id)} color="error">
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => !submitting && setDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <Box sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #059669 100%)', px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <PictureAsPdfIcon sx={{ color: 'white', fontSize: 28 }} />
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>
            {editingId ? 'Sửa điều khoản' : 'Thêm điều khoản mới'}
          </Typography>
        </Box>
        <DialogContent sx={{ px: 3, py: 2.5 }}>
          <TextField
            fullWidth size="small" label="Tiêu đề *" value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />
          <TextField
            fullWidth size="small" label="Mô tả" value={form.description} multiline rows={2}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
          />

          {/* Assign users */}
          <FormControl fullWidth size="small" sx={{ mb: 2 }}>
            <InputLabel>Gán cho nhân viên (bỏ trống = tất cả)</InputLabel>
            <Select
              multiple
              value={form.assignedUsers}
              onChange={(e) => setForm({ ...form, assignedUsers: e.target.value })}
              input={<OutlinedInput label="Gán cho nhân viên (bỏ trống = tất cả)" />}
              renderValue={(selected) => (
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {selected.map(id => {
                    const u = users.find(u => u._id === id);
                    return <Chip key={id} label={u?.fullName || id} size="small" />;
                  })}
                </Stack>
              )}
              sx={{ borderRadius: 2 }}
            >
              {users.map(u => (
                <MenuItem key={u._id} value={u._id}>
                  <Checkbox checked={form.assignedUsers.includes(u._id)} size="small" />
                  <ListItemText primary={u.fullName} secondary={u.username} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* PDF upload */}
          <Box sx={{
            border: '2px dashed #cbd5e1', borderRadius: 2, p: 3,
            textAlign: 'center', cursor: 'pointer', bgcolor: '#f8fafc',
            transition: 'all 0.2s',
            '&:hover': { borderColor: '#2563eb', bgcolor: '#eff6ff' },
          }}
            component="label"
          >
            <UploadFileIcon sx={{ fontSize: 40, color: '#94a3b8', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              {form.file ? form.file.name : (editingId ? 'Upload PDF mới (không bắt buộc)' : 'Chọn file PDF *')}
            </Typography>
            <Typography variant="caption" color="text.secondary">Tối đa 20MB</Typography>
            <input type="file" hidden accept="application/pdf" onChange={(e) => {
              if (e.target.files[0]) setForm({ ...form, file: e.target.files[0] });
            }} />
          </Box>
          {editingId && (
            <Alert severity="info" sx={{ mt: 1.5, borderRadius: 2 }}>
              Upload file PDF mới sẽ tăng phiên bản và yêu cầu nhân viên đọc & đồng ý lại.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => { setDialogOpen(false); resetForm(); }} disabled={submitting} sx={{ borderRadius: 2 }}>Hủy</Button>
          <Button
            variant="contained" onClick={handleSubmit} disabled={submitting}
            sx={{ borderRadius: 2, bgcolor: '#1e3a8a', fontWeight: 600 }}
          >
            {submitting ? <CircularProgress size={20} /> : (editingId ? 'Cập nhật' : 'Tạo mới')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detailDialog} onClose={() => setDetailDialog(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {detailDialog && (
          <>
            <Box sx={{ background: 'linear-gradient(135deg, #1e3a8a 0%, #059669 100%)', px: 3, py: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" sx={{ color: 'white', fontWeight: 700 }}>{detailDialog.title}</Typography>
              <IconButton onClick={() => setDetailDialog(null)} sx={{ color: 'white' }}><CloseIcon /></IconButton>
            </Box>
            <DialogContent sx={{ px: 3, py: 2.5 }}>
              {detailDialog.description && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{detailDialog.description}</Typography>
              )}
              <Stack spacing={1} sx={{ mb: 2 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ width: 80 }}>Phiên bản:</Typography>
                  <Chip label={`v${detailDialog.version}`} size="small" sx={{ fontWeight: 700, bgcolor: '#e0f2fe', color: '#0369a1' }} />
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ width: 80 }}>Tạo ngày:</Typography>
                  <Typography variant="body2">{formatDate(detailDialog.createdAt)}</Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography variant="caption" color="text.secondary" sx={{ width: 80 }}>Tạo bởi:</Typography>
                  <Typography variant="body2">{detailDialog.createdBy?.fullName || '—'}</Typography>
                </Stack>
              </Stack>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
                Trạng thái đồng ý ({detailDialog.acceptances?.filter(a => a.version === detailDialog.version).length || 0} đã đồng ý)
              </Typography>

              {/* Show acceptance status for each assigned user */}
              {(() => {
                const targetUsers = detailDialog.assignedUsers?.length > 0
                  ? detailDialog.assignedUsers
                  : users;
                return targetUsers.map(u => {
                  const uid = u._id;
                  const acceptance = detailDialog.acceptances?.find(
                    a => a.userId?._id === uid && a.version === detailDialog.version
                  );
                  return (
                    <Stack key={uid} direction="row" spacing={1} alignItems="center" sx={{ py: 0.75, borderBottom: '1px solid #f1f5f9' }}>
                      {acceptance ? (
                        <CheckCircleIcon sx={{ fontSize: 18, color: '#16a34a' }} />
                      ) : (
                        <PendingIcon sx={{ fontSize: 18, color: '#f59e0b' }} />
                      )}
                      <Typography variant="body2" sx={{ flex: 1 }}>{u.fullName}</Typography>
                      {acceptance ? (
                        <Typography variant="caption" color="#16a34a" fontWeight={600}>
                          Đã đồng ý {formatDate(acceptance.acceptedAt)}
                        </Typography>
                      ) : (
                        <Typography variant="caption" color="#f59e0b" fontWeight={600}>Chưa đồng ý</Typography>
                      )}
                    </Stack>
                  );
                });
              })()}
            </DialogContent>
          </>
        )}
      </Dialog>

      {/* Confirm delete */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogContent sx={{ py: 3, px: 4 }}>
          <Typography fontWeight={600}>Xác nhận xóa điều khoản này?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>Hành động này không thể hoàn tác.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)} sx={{ borderRadius: 2 }}>Hủy</Button>
          <Button variant="contained" color="error" onClick={() => handleDelete(confirmDelete)} sx={{ borderRadius: 2 }}>Xóa</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={notification.open} autoHideDuration={3000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={notification.severity} sx={{ borderRadius: 2 }}>{notification.message}</Alert>
      </Snackbar>
    </Box>
  );
};

export default AgreementManagement;
